import type {ChangeEvent, ReactNode} from 'react';
import {X} from 'lucide-react';

/**
 * The building blocks every app in this workspace is made of.
 *
 * They carry the LAYOUT (sizes, padding, rhythm); the LOOK lives in the
 * component classes in `index.css` (`surface`, `sunken`, `control`, `btn`,
 * `chip`). That split is why this file is identical in every workspace in the
 * family while each one looks nothing like the others — and why the spacing
 * contract at the top of `index.css` can be enforced in one place.
 *
 * Two rules run through all of them:
 *  - controls are at least 44px tall until the CONTAINER (not the viewport) is
 *    wide enough to be a mouse-driven layout, and
 *  - text inputs are 16px on the touch layout, because iOS Safari zooms the
 *    page the moment it focuses anything smaller.
 */

/** Height 44/36, radius 14, padding 14 — the one control geometry. */
const CONTROL =
  'control w-full min-h-11 rounded-control px-3.5 text-[16px] @xl:min-h-9 @xl:text-[13px]';

/** App root: owns the container context, the scroll area and the panel layer. */
export function Screen({children, toolbar}: {children: ReactNode; toolbar?: ReactNode}) {
  return (
    <div className="@container relative flex h-full flex-col bg-canvas text-ink">
      {toolbar ? (
        <div className="bar relative z-10 shrink-0 px-4 py-3">
          {toolbar}
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">{children}</div>
    </div>
  );
}

export function Toolbar({children}: {children: ReactNode}) {
  // In a toolbar a control sizes to its content: the `w-full` that form fields
  // need would otherwise make every <select> claim its own row.
  return <div className="flex flex-wrap items-center gap-3 [&_select]:w-auto">{children}</div>;
}

export function SearchInput({
  onChange,
  placeholder = 'Tìm kiếm…',
  value,
}: {
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
}) {
  return (
    <input
      type="search"
      value={value}
      placeholder={placeholder}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      // Its own row while the container is narrow (a 110px search box is
      // useless); shares the toolbar row once there is space.
      className={`${CONTROL} min-w-0 flex-1 basis-full @xl:max-w-64 @xl:basis-auto`}
    />
  );
}

export function Select({
  onChange,
  options,
  value,
}: {
  onChange: (value: string) => void;
  options: Array<{label: string; value: string}>;
  value: string;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={`${CONTROL} max-w-full appearance-none pr-9 font-medium [background-image:linear-gradient(45deg,transparent_50%,var(--color-ink-3)_50%),linear-gradient(135deg,var(--color-ink-3)_50%,transparent_50%)] [background-position:calc(100%-18px)_center,calc(100%-13px)_center] [background-repeat:no-repeat] [background-size:5px_5px,5px_5px]`}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function Field({
  children,
  hint,
  label,
  wide,
}: {
  children: ReactNode;
  hint?: string;
  label: string;
  wide?: boolean;
}) {
  return (
    <label className={`block ${wide ? '@lg:col-span-2' : ''}`}>
      <span className="mb-1.5 block text-[12px] font-semibold text-ink-2">{label}</span>
      {children}
      {hint ? <span className="mt-1.5 block text-[11px] text-ink-3">{hint}</span> : null}
    </label>
  );
}

export function TextInput({
  onChange,
  placeholder,
  type = 'text',
  value,
}: {
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'date' | 'datetime-local' | 'number' | 'tel' | 'text' | 'time';
  value: string | number;
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className={CONTROL}
    />
  );
}

export function TextArea({
  onChange,
  placeholder,
  rows = 3,
  value,
}: {
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  value: string;
}) {
  return (
    <textarea
      rows={rows}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className={`${CONTROL} resize-none py-3 leading-relaxed`}
    />
  );
}

type ButtonTone = 'danger' | 'ghost' | 'primary';

const TONE: Record<ButtonTone, string> = {
  danger: 'btn btn-danger',
  ghost: 'btn',
  primary: 'btn btn-primary',
};

export function Button({
  children,
  disabled,
  icon,
  onClick,
  tone = 'ghost',
  type = 'button',
  wide,
}: {
  children?: ReactNode;
  disabled?: boolean;
  icon?: ReactNode;
  onClick?: () => void;
  tone?: ButtonTone;
  type?: 'button' | 'submit';
  wide?: boolean;
}) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-control px-4 text-[13px] font-semibold disabled:cursor-not-allowed @xl:min-h-9 ${TONE[tone]} ${
        wide ? 'w-full' : ''
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

export type Tone = 'amber' | 'emerald' | 'rose' | 'sky' | 'slate' | 'violet';

export function Pill({children, tone = 'slate'}: {children: ReactNode; tone?: Tone}) {
  return (
    <span
      className={`chip tone-${tone} inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold leading-none`}
    >
      {children}
    </span>
  );
}

export function Stat({hint, label, value}: {hint?: string; label: string; value: ReactNode}) {
  return (
    <div className="surface rounded-card p-4">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-ink-3">{label}</p>
      <p className="mt-2 text-[19px] font-bold leading-none text-ink @xl:text-[22px]">{value}</p>
      {hint ? <p className="mt-2 text-[11px] leading-snug text-ink-3">{hint}</p> : null}
    </div>
  );
}

export function Card({
  action,
  children,
  title,
}: {
  action?: ReactNode;
  children: ReactNode;
  title?: string;
}) {
  return (
    <section className="surface rounded-card p-4">
      {title ? (
        <header className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-[13px] font-bold tracking-tight text-ink">{title}</h2>
          {action}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function EmptyState({hint, title}: {hint?: string; title: string}) {
  return (
    <div className="sunken rounded-card p-8 text-center">
      <p className="text-[13px] font-semibold text-ink-2">{title}</p>
      {hint ? <p className="mt-2 text-[12px] text-ink-3">{hint}</p> : null}
    </div>
  );
}

/**
 * Create / edit surface.
 *
 * It covers the app it belongs to instead of the whole screen: on the desktop
 * that keeps other windows usable, and on a phone it is simply full-screen —
 * the same component, no portal, no z-index bookkeeping against the shell.
 */
export function Panel({
  children,
  footer,
  onClose,
  title,
  wide,
}: {
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  title: string;
  /** For content that is itself a layout — a photo gallery, a comparison. */
  wide?: boolean;
}) {
  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-canvas @xl:items-center @xl:justify-center @xl:bg-[var(--scrim)] @xl:p-6">
      <div
        className={`panel-frame flex min-h-0 w-full flex-1 flex-col bg-canvas @xl:h-auto @xl:max-h-full @xl:flex-none @xl:rounded-shell ${
          wide ? '@xl:max-w-4xl' : '@xl:max-w-2xl'
        }`}
      >
        <header className="flex h-14 shrink-0 items-center gap-3 px-4">
          <h2 className="min-w-0 flex-1 truncate text-[15px] font-bold text-ink @xl:text-[14px]">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Đóng"
            onClick={onClose}
            className="btn -mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-control @xl:h-9 @xl:w-9"
          >
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4">{children}</div>

        {footer ? <div className="shrink-0 p-4">{footer}</div> : null}
      </div>
    </div>
  );
}

/** Two-column form grid that collapses to one column in a narrow container. */
export function FormGrid({children}: {children: ReactNode}) {
  return <div className="grid gap-4 @lg:grid-cols-2">{children}</div>;
}

/**
 * A row of mutually exclusive choices.
 *
 * Preferred over a <select> when there are two or three options and the choice
 * changes what the whole screen shows (Bán / Cho thuê): a dropdown hides the
 * alternatives, and hiding them is what makes people miss half the market.
 */
export function Segmented<T extends string>({
  onChange,
  options,
  value,
}: {
  onChange: (value: T) => void;
  options: Array<{label: string; value: T}>;
  value: T;
}) {
  return (
    <div className="sunken-sm inline-flex rounded-control p-1" role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          onClick={() => onChange(option.value)}
          className={`min-h-9 whitespace-nowrap rounded-[10px] px-3.5 text-[12.5px] font-semibold transition-colors ${
            option.value === value ? 'tile-active' : 'text-ink-2'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Multi-select as chips: the whole set stays visible while choosing from it. */
export function ChipToggle({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`min-h-9 rounded-full px-3.5 text-[12.5px] font-semibold transition-colors ${
        active ? 'tile-active' : 'tile text-ink-2'
      }`}
    >
      {children}
    </button>
  );
}

export function CheckRow({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex min-h-11 cursor-pointer items-center gap-3 text-[13px] text-ink-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-[18px] w-[18px] shrink-0 accent-[var(--color-accent)]"
      />
      <span className="min-w-0">{label}</span>
    </label>
  );
}

/** A slider with the value written above it, because a slider alone lies. */
export function RangeField({
  format,
  label,
  max,
  min,
  onChange,
  step,
  value,
}: {
  format: (value: number) => string;
  label: string;
  max: number;
  min: number;
  onChange: (value: number) => void;
  step: number;
  value: number;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline justify-between gap-3">
        <span className="text-[12px] font-semibold text-ink-2">{label}</span>
        <span className="text-[12.5px] font-bold tabular-nums text-ink">{format(value)}</span>
      </span>
      <input
        type="range"
        className="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}
